import { useEstimateStore } from '@talon/web/src/context';
import { EstimateStatus } from '@talon/web/lib/api';
import dayjs from 'dayjs';
import { Box, Card, Menu, Text } from 'grommet';
import { More } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddNewButton, ConfirmModal, CreateEstimateModal, EmptyState, StatusPill } from '..';

export interface IEstimateListProps {
  gridArea?: string;
}

export const EstimateList = observer(({ gridArea }: IEstimateListProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();
  const navigate = useNavigate();

  /** State **/
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hoveredId, setHoveredId] = useState<string>();
  const [focusedId, setFocusedId] = useState<string>();
  const [deletingId, setDeletingId] = useState<string>();

  /** Methods **/

  const deleteEstimate = async (id: string) => {
    await estimateStore.deleteEstimate(id);
  };

  /** Render **/
  const estimates = estimateStore.estimateList?.filter((estimate) => !estimate.is_hidden) ?? [];
  const toDelete = estimates.find((estimate) => estimate.id === deletingId);

  return (
    <Box pad="medium" overflow={{ vertical: 'auto' }} id="Estimate-list" gridArea={gridArea}>
      <Card background="white" pad="12px" gap="xsmall" flex={false}>
        <Box direction="row" align="center" justify="between" wrap gap="small" pad={{ horizontal: 'xsmall' }}>
          <Text size="17px" weight={600} color="text-heading">
            Estimates
          </Text>
          <AddNewButton plain disabled={creating} onClick={() => setShowModal(true)} label="New estimate" />
        </Box>
        {estimates.map((e) => {
          const active = e.id === estimateStore.currentEstimate?.id;
          const showMenu = active || hoveredId === e.id || focusedId === e.id;
          return (
            <Box
              key={e.id}
              role="link"
              aria-current={active ? 'page' : undefined}
              direction="row"
              align="start"
              gap="xsmall"
              round="8px"
              pad={{ vertical: '10px', horizontal: '12px' }}
              background={active ? 'brand-tint' : hoveredId === e.id ? 'surface-subtle' : undefined}
              onMouseEnter={() => setHoveredId(e.id)}
              onMouseLeave={() => setHoveredId(undefined)}
              onFocus={() => setFocusedId(e.id)}
              onBlur={(event) => !event.currentTarget.contains(event.relatedTarget) && setFocusedId(undefined)}
              onClick={() => navigate(e.id)}
              style={{ cursor: 'pointer' }}
            >
              {/* The whole title, wrapping, with its status and when it last changed */}
              <Box flex gap="xxsmall">
                <Text size="small" weight={600} color={active ? 'brand' : 'text-heading'}>
                  {e.title}
                </Text>
                <Box direction="row" gap="xsmall" align="center" wrap>
                  <StatusPill label={e.status} tone={e.status === EstimateStatus.Final ? 'ok' : 'neutral'} />
                  <Text size="xsmall" color="text-secondary">
                    Updated {dayjs(e.updated_at).format('MMM D, YYYY')}
                  </Text>
                </Box>
              </Box>
              {/* Shown on hover or focus and for the open estimate (still a tab stop when not shown); clicks and Enter here don't open it */}
              <Box
                flex={false}
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
                style={{ opacity: showMenu ? 1 : 0 }}
              >
                <Menu
                  icon={<More size="16px" color="text-meta" />}
                  a11yTitle={`More actions for ${e.title}`}
                  dropAlign={{ top: 'bottom', right: 'right' }}
                  dropProps={{ background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium' }}
                  items={[{ label: 'Delete', onClick: () => setDeletingId(e.id) }]}
                />
              </Box>
            </Box>
          );
        })}
        {!estimates.length && (
          <EmptyState
            title="No estimates yet"
            body="Create one from your sessions and usage."
            actionLabel="New estimate"
            onAction={() => setShowModal(true)}
          />
        )}
      </Card>
      {showModal && <CreateEstimateModal creating={creating} setCreating={setCreating} setShowModal={setShowModal} />}
      {toDelete && (
        <ConfirmModal
          title={`Delete ${toDelete.title}?`}
          content="Its session and usage estimates go with it."
          confirmLabel="Delete"
          danger
          onConfirm={async () => {
            setDeletingId(undefined);
            await deleteEstimate(toDelete.id);
          }}
          onCancel={() => setDeletingId(undefined)}
        />
      )}
    </Box>
  );
});
